import { createRequest } from '@/http'

// 用 createRequest 不用 axios.create —— 後者不繼承攔截器，後端回的中文訊息會被吃掉
const trendAnalyticsRequest = createRequest({
  baseURL: '/api/trendanalytics'
})

// 自主分析：案場主檔 02／03 維護（Cloud SQL trend_analytics，見 ADR-0011）
// 兩張表的操作形狀一樣，所以只有一組函式，表名當參數。
// table = 'inverter_topology_nodes' | 'node_voltage_configs'

/** 兩張表的欄位定義。表單欄位是照這份長出來的，前端不另外抄一份 */
export const getMasterDataSchemaApi = () =>
  trendAnalyticsRequest.get('/schema').then((res) => res.data)

/**
 * 案場下拉選單的內容：全部案場（代號 + 名稱）＋各自已經有幾列主檔資料。
 * 清單來自 Cosmos factories_info，不是 Cloud SQL 的同名表（那張沒有 f001）
 * @returns {Promise<{data: {code: string, name: string, rows: number}[]}>}
 */
export const getMasterDataSitesApi = () =>
  trendAnalyticsRequest.get('/sites').then((res) => res.data)

/**
 * @param {string} table
 * @param {string} [siteCode] 不給就全撈
 */
export const getMasterDataApi = (table, siteCode) =>
  trendAnalyticsRequest
    .get(`/${table}`, { params: siteCode ? { siteCode } : {} })
    .then((res) => res.data)

export const createMasterDataApi = (table, row) =>
  trendAnalyticsRequest.post(`/${table}`, row).then((res) => res.data)

/** row 要帶 key: { site_code, node_id }；主鍵本身不給改 */
export const updateMasterDataApi = (table, row) =>
  trendAnalyticsRequest.put(`/${table}`, row).then((res) => res.data)

/** @param {{site_code: string, node_id: string}[]} keys */
export const deleteMasterDataApi = (table, keys) =>
  trendAnalyticsRequest
    .delete(`/${table}`, { data: { keys } })
    .then((res) => res.data)

/**
 * 一鍵自動補上：選好的幾列，指定欄位一次填成同一個值。整批一個 transaction
 * @param {{site_code: string, node_id: string}[]} keys
 * @param {object} patch { 欄位名: 值 }
 */
export const bulkPatchMasterDataApi = (table, keys, patch) =>
  trendAnalyticsRequest
    .post(`/${table}/bulk`, { keys, patch })
    .then((res) => res.data)

/**
 * 由 factories_min 的遙測推出建議值。**只回不寫入**。
 *
 * 02 推的是「有哪些節點」；03 是拿 02 已經建好的節點推「每個節點的標準電壓」，
 * 所以 03 一定要先有 02。
 *
 * @param {string} table
 * @param {string} siteCode
 * @param {object} [opts]
 * @param {string} [opts.date] 取樣日期 yyyy-MM-dd，不給就從今天往前找有資料的那天
 * @param {number} [opts.moduleVoc] 只有 03 用：單片模組開路電壓 V（模組規格書，遙測裡沒有）
 * @param {number} [opts.moduleCount] 只有 03 用：串接片數（主檔 06，遙測裡沒有）
 * @param {'mppt'} [opts.from] 只有 03 用：給 'mppt' 就改從主檔 05 的分組產 MPPT 列，不取樣遙測
 * @returns {Promise<{data: {date, samples, inv, str, rows, skipped}}>}
 */
export const suggestMasterDataApi = (table, siteCode, opts = {}) => {
  const params = { siteCode }
  for (const k of ['date', 'moduleVoc', 'moduleCount', 'from']) if (opts[k]) params[k] = opts[k]
  return trendAnalyticsRequest.get(`/${table}/suggest`, { params }).then((res) => res.data)
}

/** 整批寫入，一個 transaction。節點編號已存在的略過，不覆蓋手動建好的列 */
export const importMasterDataApi = (table, rows) =>
  trendAnalyticsRequest.post(`/${table}/import`, { rows }).then((res) => res.data)

/**
 * 即時頁「INV 狀態」欄：每台 INV 的 { 狀態碼: 狀態名稱 }，依 inv_index 排，04 沒列到的是 null。
 * 不需要自主分析權限
 */
export const getInvStatusNamesApi = (siteCode) =>
  trendAnalyticsRequest
    .get('/inv-status-names', { params: { siteCode } })
    .then((res) => res.data)
